import { useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { DescriptorType, FilterExpression, FilterGroup, FilterOperator } from '../../../../../../../models/Account'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { IntegrationSettings } from '../../../schema'
import { Button } from '../../../../../../../components/Button/Button'
import { FilterGroupBuilder } from '../FilterGroup/FilterGroupBuilder'
import { EnforcedFilterCard } from '../FilterGroup/EnforcedFilterCard'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../../hooks'

interface SetOwnershipSectionProps {
  serviceId: IntegrationServiceId
}

export const SetOwnershipSection = ({ serviceId }: SetOwnershipSectionProps) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()

  const mappings = watch('mappings')
  const contactObject = useMemo(() => mappings?.contact?.objectName ?? null, [mappings])
  const ownerFilterGroup = useMemo(() => mappings?.contact?.sync?.ownerFilterGroup ?? null, [mappings]) as FilterGroup | null
  const applyOwnerFilter = useMemo(() => mappings?.contact?.sync?.applyOwnerFilter, [mappings])

  const [isConfiguring, setIsConfiguring] = useState<boolean>(false)
  const hasFilters = (ownerFilterGroup?.filters?.length ?? 0) > 0
  const showBuilder = isConfiguring || hasFilters

  const accountId = useAppSelector(selectAccountId)
  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    { accountId, connector: serviceId, context: { objectName: contactObject! } },
    { skip: !contactObject || !accountId },
  )

  const formFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])
  const relationships: IntegrationObjectRelationship[] = useMemo(() => describe?.data?.describe?.relationships || [], [describe])

  const setFormOwnerFilterGroup = (newGroup: FilterGroup | null) => {
    setValue(
      'mappings',
      {
        ...mappings,
        contact: {
          ...mappings?.contact,
          sync: { ...(mappings?.contact?.sync ?? {}), ownerFilterGroup: newGroup },
        },
      },
      { shouldDirty: true },
    )
  }

  const setOwnerFilterGroup = (newFilterGroup: FilterGroup | null) => {
    const nonEmpty = newFilterGroup !== null && (newFilterGroup.filters?.length ?? 0) > 0
    setFormOwnerFilterGroup(nonEmpty ? newFilterGroup : null)
  }

  const handleConfigure = () => setIsConfiguring(true)

  const handleRemoveOwnerFilter = () => {
    setIsConfiguring(false)
    setFormOwnerFilterGroup(null)
  }

  const enforcedPlaceholder: FilterExpression = {
    field: `${getIntegrationDisplayName(serviceId)} Owner ID`,
    operator: FilterOperator.EQ,
    source: { value: 'currentUser', type: DescriptorType.STATIC },
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4 className='mb-2'>Ownership Filter</h4>
          <p className='zocks-text-gray-600 mb-0'>
            Control which contacts are returned based on record ownership. By default, Zocks only fetches contacts owned by the syncing advisor.
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          {showBuilder ? (
            <FilterGroupBuilder
              serviceId={serviceId}
              value={ownerFilterGroup}
              onChange={setOwnerFilterGroup}
              availableFormFieldDefinitions={formFieldDefinitions}
              availableRelationships={relationships}
              loading={isDescribeFetching}
              placeholders={[]}
            />
          ) : applyOwnerFilter !== false ? (
            <EnforcedFilterCard conditions={[enforcedPlaceholder]} />
          ) : null}
          <div className='d-flex justify-content-end mt-2'>
            {showBuilder ? (
              <Button type='button' variant='link' className='p-0 zocks-text-gray-600' onClick={handleRemoveOwnerFilter}>
                Remove ownership filter
              </Button>
            ) : (
              <Button type='button' variant='link' className='p-0' onClick={handleConfigure}>
                Configure ownership filter
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
